import { CalendarIcon, ChevronRightIcon, TicketIcon } from "@heroicons/react/outline";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function EventCard({ event }) {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language?.startsWith("en") ? "en-GB" : "es-ES";

  const formatEventDate = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);

    const options = {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };

    const formattedStart = startDate.toLocaleString(currentLang, options);
    const formattedEnd = endDate.toLocaleString(currentLang, options);

    return `${formattedStart} — ${formattedEnd}`;
  };

  const navigate = useNavigate();

  return (
    <div
      className="flex bg-[#02150d] border border-green-900/30 rounded-xl overflow-hidden hover:border-green-500/40 transition cursor-pointer"
      onClick={() =>
        navigate(`/tickets-store/events/${event.id}`, { state: event })
      }
    >
      {/* LEFT STRIP */}
      <div className="relative w-28 md:w-36 aspect-[9/16] flex-shrink-0 overflow-hidden bg-[#032017]">
        {event.flyer_portrait ? (
          <>
            <img
              className="w-full h-full object-cover"
              src={event.flyer_portrait}
              alt={event.name}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          </>
        ) : (
          <div className="w-full h-full" />
        )}
      </div>

      {/* CONTENT */}
      <div className="flex-1 p-4 flex flex-col justify-between md:flex-row md:items-center gap-4">
        {/* LEFT CONTENT */}
        <div className="flex-1">

          {/* BUY BUTTON — top right */}
          <div className="flex justify-end mb-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/tickets-store/events/${event.id}`, { state: event });
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-green-500 text-green-300 text-[10px] tracking-widest uppercase font-medium hover:bg-green-500 hover:text-black transition"
            >
              <TicketIcon className="w-3 h-3" />
              {t("eventCard.buyTickets")}
              <ChevronRightIcon className="w-3 h-3" />
            </button>
          </div>

          {/* NAME */}
          <h3 className="font-semibold">{event.name}</h3>

          {/* DATE */}
          <div className="flex items-center mt-1">
            <CalendarIcon className="w-4 h-4 text-gray-400 mr-1" />
            <p className="text-sm text-gray-400">
              {formatEventDate(event.date_start, event.date_end)}
            </p>
          </div>

          {event.sale_tag !== null && (
            <div className="mt-1">
              <span
                className="text-[10px] tracking-widest uppercase px-2 py-[4px] rounded-md font-medium"
                style={{
                  backgroundColor: `${event.sale_tag?.color}70`,
                  color: event.sale_tag?.color,
                  border: `1px solid ${event.sale_tag?.color}`,
                }}
              >
                {event.sale_tag?.label}
              </span>
            </div>
          )}

          {/* TAGS */}
          <div className="mt-3 flex flex-col gap-2">
            <div className="flex gap-2 flex-wrap">
              {event.meta_tags.map((tag, i) => (
                <span
                  key={i}
                  className="text-[10px] tracking-wider uppercase px-2 py-[4px] 
                    bg-[#031b14] border border-green-700/40 rounded-md"
                >
                  {tag}
                </span>
              ))}
            </div>

            {/* CATEGORY (BOTTOM RIGHT) */}
            {event.category && (
              <div className="flex justify-end">
                <span
                  className="text-[10px] tracking-widest uppercase px-2 py-[4px] 
                    bg-green-700/20 text-green-300 
                    border border-green-500/30 rounded-md whitespace-nowrap"
                >
                  {event.category}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}